import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { FlaskConical, Trophy, Plus, Loader2, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  birthday_30d: "30-Day Countdown", birthday_21d: "21-Day", birthday_14d: "14-Day",
  birthday_7d: "7-Day", birthday_1d: "Day Before", expiring_soon: "Expiring Deals",
  new_deals_weekly: "Weekly Digest", deal_updated: "Deal Updated",
  badge_earned: "Badge Earned", level_up: "Level Up",
  monthly_challenge: "Monthly Challenge", streak_at_risk: "Streak at Risk",
  whats_new: "What's New", beta_updates: "Beta Updates", manual: "Manual Broadcast",
};

export default function ABTestResults() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/notifications/ab-results`);
      setData(d);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-3" data-testid="ab-test-section">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
          <FlaskConical className="w-3.5 h-3.5" /> A/B Copy Tests
        </p>
        <button onClick={() => setShowForm(!showForm)} className="p-1 rounded-lg hover:bg-slate-100 transition" data-testid="ab-create-btn">
          {showForm ? <X className="w-3.5 h-3.5 text-slate-400" /> : <Plus className="w-3.5 h-3.5 text-slate-400" />}
        </button>
      </div>

      {showForm && <ABTestForm onCreated={() => { setShowForm(false); load(); }} />}

      {/* Active Tests */}
      {data?.active_tests?.length > 0 && <ActiveTests tests={data.active_tests} onDeactivated={load} />}

      {/* Results Table */}
      {data?.results?.length > 0 ? <ResultsTable results={data.results} /> : (
        <p className="text-[10px] text-slate-400 text-center py-3">No A/B test data yet. Create a test and send notifications to see results.</p>
      )}
    </div>
  );
}

function ActiveTests({ tests, onDeactivated }) {
  const handleDeactivate = async (type) => {
    try {
      await axios.delete(`${API}/admin/notifications/ab-test/${type}`);
      toast.success("Test deactivated");
      onDeactivated();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-1.5" data-testid="ab-active-tests">
      {tests.map(t => (
        <div key={t.notification_type} className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
          <FlaskConical className="w-3 h-3 text-indigo-400 flex-shrink-0" />
          <span className="text-[10px] font-medium text-indigo-700 flex-1 truncate">{TYPE_LABELS[t.notification_type] || t.notification_type}</span>
          <button onClick={() => handleDeactivate(t.notification_type)} className="p-0.5 rounded hover:bg-indigo-100" data-testid={`ab-deactivate-${t.notification_type}`}>
            <Trash2 className="w-3 h-3 text-indigo-300" />
          </button>
        </div>
      ))}
    </div>
  );
}

function ResultsTable({ results }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden" data-testid="ab-results-table">
      <div className="grid grid-cols-[1fr_32px_44px_44px_44px] gap-0 px-3 py-1.5 bg-slate-50 border-b border-slate-100">
        <span className="text-[9px] font-bold text-slate-400 uppercase">Type</span>
        <span className="text-[9px] font-bold text-slate-400 uppercase text-center">V</span>
        <span className="text-[9px] font-bold text-slate-400 uppercase text-right">Sent</span>
        <span className="text-[9px] font-bold text-slate-400 uppercase text-right">Open%</span>
        <span className="text-[9px] font-bold text-slate-400 uppercase text-right">CTR%</span>
      </div>
      {results.map(r => (
        <div key={r.notification_type}>
          {r.variants.map(v => (
            <div key={`${r.notification_type}-${v.variant}`} className={`grid grid-cols-[1fr_32px_44px_44px_44px] gap-0 px-3 py-1.5 ${v.variant === "B" ? "bg-slate-50/50" : ""}`} data-testid={`ab-row-${r.notification_type}-${v.variant}`}>
              <span className="text-[10px] font-medium text-slate-700 truncate">
                {v.variant === "A" ? (TYPE_LABELS[r.notification_type] || r.notification_type) : ""}
              </span>
              <span className={`text-[10px] font-bold text-center ${v.variant === r.winner ? "text-emerald-600" : "text-slate-400"}`}>
                {v.variant}{v.variant === r.winner && <Trophy className="w-2.5 h-2.5 inline ml-0.5 -mt-0.5" />}
              </span>
              <span className="text-[10px] text-slate-600 text-right font-mono">{v.sent}</span>
              <span className="text-[10px] text-emerald-600 text-right font-mono">{v.open_rate}%</span>
              <span className="text-[10px] text-amber-600 text-right font-mono">{v.click_rate}%</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ABTestForm({ onCreated }) {
  const [type, setType] = useState("birthday_7d");
  const [aTitle, setATitle] = useState("");
  const [aBody, setABody] = useState("");
  const [bTitle, setBTitle] = useState("");
  const [bBody, setBBody] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!aTitle || !aBody || !bTitle || !bBody) return toast.error("All fields required");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/notifications/ab-test`, {
        notification_type: type,
        variant_a_title: aTitle, variant_a_body: aBody,
        variant_b_title: bTitle, variant_b_body: bBody,
      });
      toast.success("A/B test created");
      onCreated();
    } catch { toast.error("Failed to create test"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-indigo-50/50 rounded-xl border border-indigo-100 p-3 space-y-2" data-testid="ab-test-form">
      <select value={type} onChange={e => setType(e.target.value)} className="w-full px-2 py-1.5 bg-white rounded-lg text-[10px] border border-slate-200" data-testid="ab-type-select">
        {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <p className="text-[9px] font-bold text-indigo-500 uppercase">Variant A</p>
          <input value={aTitle} onChange={e => setATitle(e.target.value)} placeholder="Title A" className="w-full px-2 py-1 bg-white rounded text-[10px] border border-slate-200" data-testid="ab-a-title" />
          <input value={aBody} onChange={e => setABody(e.target.value)} placeholder="Body A" className="w-full px-2 py-1 bg-white rounded text-[10px] border border-slate-200" data-testid="ab-a-body" />
        </div>
        <div className="space-y-1">
          <p className="text-[9px] font-bold text-violet-500 uppercase">Variant B</p>
          <input value={bTitle} onChange={e => setBTitle(e.target.value)} placeholder="Title B" className="w-full px-2 py-1 bg-white rounded text-[10px] border border-slate-200" data-testid="ab-b-title" />
          <input value={bBody} onChange={e => setBBody(e.target.value)} placeholder="Body B" className="w-full px-2 py-1 bg-white rounded text-[10px] border border-slate-200" data-testid="ab-b-body" />
        </div>
      </div>
      <button onClick={handleSubmit} disabled={saving} className="w-full py-1.5 bg-indigo-500 text-white rounded-lg text-[10px] font-bold hover:bg-indigo-600 transition disabled:opacity-50" data-testid="ab-submit-btn">
        {saving ? "Creating..." : "Create A/B Test"}
      </button>
    </div>
  );
}
